import { BrowserModule } from '@angular/platform-browser';
import { enableProdMode, NgModule } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { environment } from './../environments/environment';
import { resolveCordUrl } from './../environments/resolve-cord-url';

import {
    CordIamService,
    CordTaskManagementService,
    CordFinanceService,
    CordMailboxService,
    CordDocsService,
    CordStatusService,
    CordNewsService,
    CordHdnService,
    CordCreditProviderService,
    CordSubscriptionService,
    CordMidOfficeService,
    CordViewService,
    CordInterestService,
    CordNoteService,
    CordWorkflowService,
    CordLogbookService,
    CordMoneyviewService,
    CordRuleCubeService,
    CordQuestionService,
    CordEmailMergeService
} from '@yellowtail-frontend/cord-connector';
import { StorageService } from '@yellowtail-frontend/storage-service';

import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { MatTabsModule } from '@angular/material/tabs';

import { FormFieldWrapperComponent } from './screens/formly/components/form-field-wrapper/form-field-wrapper.component';
import { ObjectComponent } from './screens/formly/components/object/object.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AXLookup, TransformTextPipe } from './utilities/pipe/pipes';
import { GroupingComponent } from './screens/formly/components/grouping/grouping.component';
import { ButtonAtomModule } from './components/atoms/button/button.module';
import { AccordionAtomModule } from './components/atoms/accordion/accordion.module';
import { AccordionHeaderAtomModule } from './components/atoms/accordion-header/accordion-header.module';
import { ImageAtomModule } from './components/atoms/image/image.module';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { DirectivesModule } from '../app/directives/directives.module';
import { SnackbarAtomModule } from './components/atoms/snackbar/snackbar.module';
import { IndexScreenModule } from './screens/index/index.module';
import { ConfirmModalModule } from './modals/confirm-modal/confirm-modal.module';
import { ArrayComponent } from './screens/formly/components/array/array.component';
import { OptionalComponent } from './screens/formly/components/optional/optional.component';
import { SsoModule } from './screens/login/sso/sso.module';
import { AuthoriseScreenModule } from './screens/login/authorise/authorise/authorise.module';
import { PipeModule } from './utilities/pipe/pipe.module';
import { CurrencyPipe } from '@angular/common';
import { NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';
import { NullComponent } from './screens/formly/components/null/null.component';
import { SessionExpiredModule } from './modals/session-expired/session-expired.module';
import { CustomRouteReuseStrategy } from './utilities/custom-route-reuse.strategy';
import { RouteReuseStrategy } from '@angular/router';

if (environment.production || environment.environment.toLowerCase() === 'prd' || environment.environment.toLowerCase() === 'acc') {
    enableProdMode();
}

export function minItemsValidationMessage(err, field: FormlyFieldConfig): string {
    return `should NOT have fewer than ${field.templateOptions.minItems} items`;
}

export function maxItemsValidationMessage(err, field: FormlyFieldConfig): string {
    return `should NOT have more than ${field.templateOptions.maxItems} items`;
}

export function minlengthValidationMessage(err, field: FormlyFieldConfig): string {
    return `should NOT be shorter than ${field.templateOptions.minLength} characters`;
}

export function maxlengthValidationMessage(err, field: FormlyFieldConfig): string {
    return `should NOT be longer than ${field.templateOptions.maxLength} characters`;
}

export function minValidationMessage(err, field: FormlyFieldConfig): string {
    return `should be >= ${field.templateOptions.min}`;
}

export function maxValidationMessage(err, field: FormlyFieldConfig): string {
    return `should be <= ${field.templateOptions.max}`;
}

export function multipleOfValidationMessage(err, field: FormlyFieldConfig): string {
    return `should be multiple of ${field.templateOptions.step}`;
}

export function exclusiveMinimumValidationMessage(err, field: FormlyFieldConfig): string {
    return `should be > ${field.templateOptions.step}`;
}

export function exclusiveMaximumValidationMessage(err, field: FormlyFieldConfig): string {
    return `should be < ${field.templateOptions.step}`;
}

export function constValidationMessage(err, field: FormlyFieldConfig): string {
    return `should be equal to constant "${field.templateOptions.const}"`;
}

@NgModule({
    declarations: [
        AppComponent,
        ArrayComponent,
        ObjectComponent,
        FormFieldWrapperComponent,
        AXLookup,
        GroupingComponent,
        OptionalComponent,
        NullComponent
    ],
    imports: [
        // Required for app.component.html
        IndexScreenModule,
        ImageAtomModule,
        ButtonAtomModule,
        SnackbarAtomModule,
        // Generally Required
        BrowserModule,
        MatDialogModule,
        MatTooltipModule,
        MatFormFieldModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        FormsModule,
        MatTabsModule,
        FormlyModule.forRoot(),
        DirectivesModule,
        SessionExpiredModule,
        // These are required because not all formly components have been modularised
        AccordionAtomModule,
        ConfirmModalModule,
        AccordionHeaderAtomModule, // array formly, grouping formly, object formly
        SsoModule,
        AuthoriseScreenModule,
        PipeModule
    ],
    providers: [
        CurrencyPipe,
        TransformTextPipe,
        NgxExtendedPdfViewerService,
        {
            provide: CordHdnService, useValue: new CordHdnService(resolveCordUrl(environment.cordHdnUrl))
        },
        {
            provide: CordNewsService, useValue: new CordNewsService(resolveCordUrl(environment.cordCordMidOfficeUrl))
        },
        {
            provide: CordCreditProviderService, useValue: new CordCreditProviderService(resolveCordUrl(environment.cordCordMidOfficeUrl))
        },
        {
            provide: CordDocsService, useValue: new CordDocsService(resolveCordUrl(environment.cordDocumentsUrl))
        },
        {
            provide: CordStatusService, useValue: new CordStatusService(resolveCordUrl(environment.cordStatusUrl))
        },
        {
            provide: CordTaskManagementService, useValue: new CordTaskManagementService(resolveCordUrl(environment.cordTaskManagerUrl))
        },
        {
            provide: CordIamService, useValue: new CordIamService(resolveCordUrl(environment.cordAccessIdentityUrl))
        },
        {
            provide: CordFinanceService, useValue: new CordFinanceService(resolveCordUrl(environment.cordFinanceUrl))
        },
        {
            provide: CordMailboxService, useValue: new CordMailboxService(resolveCordUrl(environment.cordMailboxUrl))
        },
        {
            provide: CordSubscriptionService, useValue: new CordSubscriptionService(resolveCordUrl(environment.cordCordMidOfficeUrl))
        },
        {
            provide: CordViewService, useValue: new CordViewService(resolveCordUrl(environment.cordViewsUrl))
        },
        {
            provide: CordMidOfficeService, useValue: new CordMidOfficeService(resolveCordUrl(environment.cordCordMidOfficeUrl))
        },
        {
            provide: CordRuleCubeService, useValue: new CordRuleCubeService(resolveCordUrl(environment.cordRuleCubeConnectorUrl))
        },
        {
            provide: CordInterestService, useValue: new CordInterestService(resolveCordUrl(environment.cordMoneyViewConnectorUrl))
        },
        {
            provide: CordNoteService, useValue: new CordNoteService(resolveCordUrl(environment.cordCordMidOfficeUrl))
        },
        {
            provide: CordWorkflowService, useValue: new CordWorkflowService(resolveCordUrl(environment.cordWorkflowUrl))
        },
        {
            provide: CordLogbookService, useValue: new CordLogbookService(resolveCordUrl(environment.cordCordMidOfficeUrl))
        },
        {
            provide: CordMoneyviewService, useValue: new CordMoneyviewService(resolveCordUrl(environment.cordMoneyViewConnectorUrl))
        },
        {
            provide: CordQuestionService, useValue: new CordQuestionService(resolveCordUrl(environment.cordQuestionnaireUrl))
        },
        {
            provide: CordEmailMergeService, useValue: new CordEmailMergeService(resolveCordUrl(environment.cordEmailMergeServiceUrl))
        },
        { provide: StorageService, useValue: new StorageService({useEncryption: environment.storageUseEncryption, sessionTimeToLive: environment.storageSessionTimeToLive}) },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {subscriptSizing: 'dynamic'} }, // remove extra space under all mat-form-field elements
        { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy }, // Custom route reuse strategy for active menu item refresh behaviour
        provideHttpClient(withInterceptorsFromDi()),
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
